import React from 'react';
import App from 'next/app';
import Router from 'next/router';
import { MDXProvider } from '@mdx-js/react';
import * as Sentry from '@sentry/node';
import { sentryEnabled } from 'config';
import { RewriteFrames } from '@sentry/integrations';
import SwaggerUI from 'swagger-ui-react';
import components from '~/components/md';
import Header from '~/components/header';
import Footer from '~/components/footer';
import Aside from '~/components/aside';
import Menu from '~/components/menu';
import { GlobalUiContextProvider } from '~/context/globalUiContext';
import { ArticleContextProvider } from '~/context/articleContext';
import Seo from '~/components/seo';
import CategoriesList from '~/components/categoriesList';
import * as articleHelper from '~/helpers/article';
import { getApplicationUrl } from '../helpers/url';
import * as gtag from '~/lib/gtag';

import '~/assets/scss/main.scss';
import 'swagger-ui-react/swagger-ui.css';
import ErrorPage from './_error';

Sentry.init({
  dsn: null,
  enabled: sentryEnabled,
  integrations: [new RewriteFrames({
    root: global.rootDir
  })]
});

const DEFAULT_ARTICLE = {
  path: ''
};

Router.events.on('routeChangeComplete', (url) => gtag.pageview(url));

class MyApp extends App {
  static async getInitialProps(options) {
    const { ctx } = options;
    const initialProps = await App.getInitialProps(options);

    return {
      ...initialProps,
      article: getArticle(ctx.asPath)
    };
  }

  componentDidMount() {
    Router.onRouteChangeComplete = () => {
      document.body.scrollTo(0, 0);
    };
  }

  render() {
    const { Component, pageProps, article } = this.props;

    // Workaround for https://github.com/zeit/next.js/issues/8592
    const { err } = this.props;
    const modifiedPageProps = { ...pageProps, err };

    const isApi = article.path === '/docs/api';
    const mainClass = isApi ? 'col-md-12 col-lg-9' : 'col-md-8 col-lg-6 col-xl-6_5';

    return (
      <>
        <Seo article={article} />
        <GlobalUiContextProvider>
          <ArticleContextProvider article={article}>
            <div className="full-content">
              <Header />
              <div className="container">
                <div className="row">
                  {Component === ErrorPage ? (
                    <Component {...modifiedPageProps} />
                  ) : (
                    <>
                      <Menu />
                      <main className={`${mainClass} main-docs`}>
                        <article className="post-content">
                          <MDXProvider components={components}>
                            <Component {...modifiedPageProps} />
                          </MDXProvider>
                          {isApi && <SwaggerUI url={getApplicationUrl('/api/doc.json')} />}
                          {article.type === 'CATEGORY' && <CategoriesList />}
                        </article>
                      </main>
                      {!isApi && <Aside />}
                    </>
                  )}
                </div>
              </div>
            </div>
            <Footer />
          </ArticleContextProvider>
        </GlobalUiContextProvider>
      </>
    );
  }
}

function getArticle(url) {
  return articleHelper.getArticle(url) || DEFAULT_ARTICLE;
}

export default MyApp;
